import { DomainList, DomainListItemCard } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { useDeleteAgency, useGetAgencies } from "@/hooks/agencies";
import { T } from "@/i18n";
import { Agency } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, Waypoints } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Favorite } from "./-components/favorites";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/(agencies)/agencies")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetAgencies();

  return (
    <DomainList
      domain="agencies"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="agencies.title" /> <Building2 width={16} height={16} />
            </>
          ),
          href: "/agencies",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "agencies.fields.name",
          },
          {
            header: "agencies.fields.tags",
          },
          {
            header: "agencies.fields.country",
          },
          {
            header: "agencies.fields.notes",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/agencies/new"
      renderItem={(agency, index, isTable) => (
        <AgencyCard agency={agency} key={index} isTable={isTable} />
      )}
    />
  );
}

function AgencyCard({ agency, isTable }: { agency: Agency; isTable: boolean }) {
  const { mutateAsync } = useDeleteAgency();
  const deleteAgency = () => mutateAsync(agency.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain="agencies"
      title={
        <div
          className={cn(
            "flex items-center gap-1",
            isTable && "flex-row-reverse justify-end",
          )}
        >
          <span>{agency.name}</span>
          <Favorite agency={agency} />
        </div>
      }
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={
        agency.tags?.length
          ? agency.tags.map((tag) => <Badge key={tag}>{tag}</Badge>)
          : "-"
      }
      viewLink={(children, key) => (
        <Link
          key={key}
          to="/agencies/$id"
          params={{ id: agency.id.toString() }}
        >
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/agencies/$id/edit"
          params={{ id: agency.id.toString() }}
        >
          {children}
        </Link>
      )}
      extraItems={[
        {
          label: "tourAgencies.agency.title",
          icon: () => <Waypoints />,
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/tour-agencies/$id"
              search={{ domain: "agency" }}
              params={{ id: agency.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
      ]}
      contentMap={[
        agency.country
          ? {
              label: t("agencies.fields.country"),
              value: <T tKey={`countries.${agency.country}`} />,
            }
          : null,
        {
          value: agency.notes,
          tableZoom: true,
          tableClassName:
            "max-w-56 overflow-hidden text-ellipsis whitespace-nowrap",
        },
      ]}
      onDelete={deleteAgency}
    />
  );
}
